import { DisputeDto, DisputeStatus } from './billingLine.model';

/**
 * Pure, React-free derivations over a line's chargeback, shared by the PDV receivables rows, the
 * chase dashboard and the drawer cards, following the `paymentLink.utils.ts` precedent: the rule
 * is stated once and cannot drift between surfaces. Anything needing hooks or i18n belongs in the
 * pill / `deskPayment.labels.ts`, where the rest of the dispute wording already lives.
 */

/**
 * What a line's dispute pill has to say, wording and formatting left to the pill itself:
 * - `needsResponse` — Stripe is waiting for evidence, and stops waiting at a deadline;
 * - `underReview` — the evidence is in (or the bank is looking on its own): nothing to do but wait;
 * - `won` — it closed without costing the hospital anything, whether it was argued and won or was
 *   only ever an early warning that went away;
 * - `lost` — the bank kept the money: it comes back off the line, which reopens it;
 * - `flagged` — a status Stripe added after this build. The chargeback is stated as the bare fact
 *   it is, since a token nobody here can word must not be shown to a secretary as one.
 */
export type DisputePillState =
  | {
      kind: 'needsResponse' | 'underReview' | 'won' | 'lost';
      /** The token behind the state, worded through `stripeStatusLabelKey`. */
      status: DisputeStatus;
    }
  | { kind: 'flagged' };

/** The states a status the frontend recognises can land on. */
type WordedDisputePillKind = Exclude<DisputePillState['kind'], 'flagged'>;

/**
 * Stripe's dispute statuses, mapped onto what each one asks of the desk. The early-warning trio
 * folds into its dispute-stage twin: an inquiry and a chargeback differ in whether the bank has
 * taken the money yet, not in what the hospital has to do about it — and the pill quotes the raw
 * status underneath, so the two never read as one another.
 */
const PILL_KIND_BY_STATUS: Record<DisputeStatus, WordedDisputePillKind> = {
  warning_needs_response: 'needsResponse',
  warning_under_review: 'underReview',
  // Not "won" in Stripe's sense — nothing was argued — but the same fact for the hospital: it is
  // over and the money stayed. The wording keeps to that, and never claims a dispute was won.
  warning_closed: 'won',
  needs_response: 'needsResponse',
  under_review: 'underReview',
  won: 'won',
  lost: 'lost'
};

const DISPUTE_STATUSES = Object.keys(PILL_KIND_BY_STATUS);

const isDisputeStatus = (status: string): status is DisputeStatus =>
  DISPUTE_STATUSES.includes(status);

/**
 * The dispute state a billing line shows next to its own status, or `undefined` when it carries no
 * chargeback at all. The single place the state is decided, so no two surfaces can word the same
 * chargeback differently.
 *
 * A closed dispute is kept on the row rather than dropped once it stops asking for anything: the
 * row is the only surface a chargeback surfaces on, and a chargeback that came and went is exactly
 * what explains a settled line that reopened, or a charge the patient says vanished.
 */
export const deriveDisputePill = (
  dispute: DisputeDto | null | undefined
): DisputePillState | undefined => {
  if (!dispute) {
    return undefined;
  }
  return isDisputeStatus(dispute.status)
    ? { kind: PILL_KIND_BY_STATUS[dispute.status], status: dispute.status }
    : { kind: 'flagged' };
};
